import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Clock3, GraduationCap, Activity } from "lucide-react";
import { openWhatsApp, DEFAULT_WA_MESSAGE } from "@/lib/whatsapp";

const HERO_IMG =
  "https://images.unsplash.com/photo-1758691462321-9b6c98c40f7e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxob21lJTIwbnVyc2luZyUyMGNhcmUlMjBkb2N0b3IlMjBlbGRlcmx5JTIwcGF0aWVudHxlbnwwfHx8fDE3ODg1Mzg2NDh8MA&ixlib=rb-4.1.0&q=85";

const LINES = [
  { text: "Hospital-grade care,", cls: "text-navy" },
  { text: "in the comfort", cls: "text-navy" },
  { text: "of your home.", cls: "text-tealbrand" },
];

const TRUST = [
  { icon: Clock3, label: "24/7 care dispatch" },
  { icon: GraduationCap, label: "GNM & B.Sc nurses only" },
  { icon: ShieldCheck, label: "Background-verified staff" },
];

const Hero = ({ onBookCare }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <section ref={ref} id="top" className="relative pt-32 pb-20 md:pt-40 md:pb-28" data-testid="hero-section">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-tealbrand-light/60 blur-3xl" />
        <div className="absolute bottom-0 left-1/4 h-72 w-72 rounded-full bg-tealbrand-light/40 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-6">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xs uppercase tracking-[0.25em] font-bold text-tealbrand mb-6 flex items-center gap-2"
            data-testid="hero-eyebrow"
          >
            <Activity className="h-4 w-4" /> Sharpin Healthcare Services · Delhi NCR
          </motion.p>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08]" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-6 text-base sm:text-lg text-slate-600 font-medium leading-relaxed max-w-xl"
            data-testid="hero-subtext"
          >
            Verified caretakers, GNM &amp; B.Sc qualified nurses, baby caretakers and japa
            maids — deployed to your doorstep, often the same day. Care beyond treatment.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.0 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={onBookCare}
              data-testid="hero-book-care-btn"
              className="group inline-flex items-center gap-2 bg-tealbrand text-white font-semibold px-8 py-4 rounded-full hover:bg-navy transition-colors duration-300 shadow-[0_10px_30px_rgba(13,148,136,0.3)]"
            >
              Request Care Now
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => openWhatsApp(DEFAULT_WA_MESSAGE)}
              data-testid="hero-whatsapp-btn"
              className="inline-flex items-center gap-2 border-2 border-navy/15 text-navy font-semibold px-8 py-4 rounded-full hover:border-tealbrand hover:text-tealbrand transition-colors duration-300"
            >
              Chat on WhatsApp
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
            data-testid="hero-trust-row"
          >
            {TRUST.map((t) => (
              <div key={t.label} className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <t.icon className="h-4 w-4 text-tealbrand" />
                {t.label}
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-6 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, clipPath: "inset(12% 12% 12% 12% round 2rem)" }}
            animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0% 0% 0% round 2rem)" }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-4 rounded-[2.5rem] bg-tealbrand/10 blur-2xl" aria-hidden="true" />
            <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-navy/10 shadow-2xl">
              <motion.img
                src={HERO_IMG}
                alt="Compassionate home nurse giving care to senior patient at home"
                style={{ y: imgY, scale: 1.12 }}
                className="w-full h-[420px] md:h-[520px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/40 via-transparent to-transparent" />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.3 }}
              className="absolute -bottom-6 -left-4 sm:-left-8 bg-white/90 backdrop-blur-xl border border-slate-200/60 shadow-xl rounded-2xl px-6 py-4"
              data-testid="hero-stat-card"
            >
              <p className="font-display text-2xl font-bold text-navy">Same-day</p>
              <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">
                staff deployment
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: -24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.5 }}
              className="absolute -top-6 right-4 sm:-right-4 bg-navy text-white rounded-2xl px-6 py-4 shadow-xl"
              data-testid="hero-badge-card"
            >
              <p className="font-display text-2xl font-bold text-tealbrand-light">24/7</p>
              <p className="text-xs uppercase tracking-[0.15em] text-slate-300 font-semibold">
                care coordination
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
